import React, { useState, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import QuoteBuilderForm from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderForm.jsx';
import LayoutNavSectionList from 'components/layout_and_quote_builder/LayoutNavSectionList.jsx';
import MyEventListener from 'components/common/MyEventListener.jsx';
import PostRequest from 'components/common/PostRequest.jsx';
import { buildSetupWizardStages } from 'components/frank/utils/setupWizardStages.js';
import { linkedSectionIndexes } from 'components/frank/utils/linkedNavSection.js';

// The guided setup wizard for the /mcc (Frank) edit page -- a modal that hand-holds the user through
// configuring a quote, one decision per stage, so it is always obvious what to pick next:
//
//   Stage 1  Quantity in order (default 1000)
//   then, per priceable SECTION (e.g. Carton -- NOT per sub_section: one line + one solution per section):
//     pick a manufacturing line   (the filter panel + the manufacturing-line listing; a pick just MARKS it)
//     pick a solution             (the picked plan's solution cards; a click just MARKS one) -> Done on the last
//
// It REUSES the page's own per-section flow (ManufacturingLineLists, via LayoutNavSectionList) rather than
// rebuilding it: each section stage renders that component with wizardStage set, so all of its fetching,
// scoring and solution grouping is shared. The wizard only adds the chrome (stage title, "Stage X of N", a
// progress bar that fills as you advance, Back / Next / Done) and does the select-solution POST when it leaves
// a solution stage -- the same POST SelectedSolutionSections does on the normal page. Because that POST updates
// window.user_quote_nav_sections, closing the wizard reveals the normally-rendered, now-priced page -- and
// onClose reports the section it priced, so the page opens on that section's Pricing tab.
const FrankSetupWizard = ({ navSections, config, onClose }) => {
  // Quantity in order, kept in step with the count field the quantity stage renders (it publishes
  // quote_builder.count_changed on change), so the solution cards in the section stages price against the
  // current quantity. Seeded from the quote, falling back to 1000 (the create default).
  const [count, setCount] = useState((window.user_quote && window.user_quote.count) || 1000);
  MyEventListener([
    { key: 'quote_builder.count_changed', callback: (message) => { setCount(() => message.detail); } }
  ]);

  // The stages: quantity, then a (line, solution) pair per nav section that actually CHOOSES. A linked section
  // inherits its manufacturing line and solution from another one, so the wizard never asks about it -- two
  // stages that decide nothing would imply the answer was the user's to give. The link is read from the live
  // window sections (the same source the page reads), not from navSections, so the wizard and the section flow
  // agree on which sections are linked. See buildSetupWizardStages and linkedNavSection.
  const stages = buildSetupWizardStages(navSections, linkedSectionIndexes(window.user_quote_nav_sections));
  const totalStages = stages.length;

  const [currentStage, setCurrentStage] = useState(0);
  // The marked plan / marked solution PER SECTION, filed by section index. Reported up from the mounted
  // ManufacturingLineLists via the callbacks below; read back to enable Next / Done and to know what to POST.
  const [pickedBySection, setPickedBySection] = useState({});
  const [selectionBySection, setSelectionBySection] = useState({});
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState(false);

  // The section the wizard last PRICED -- persisted a solution for -- or null while it has priced none. Handed
  // to onClose so the page can open THAT section's Pricing tab: the user has just chosen a manufacturing line
  // and a solution, so what they want to see next is the price, not the manufacturing-line listing the page
  // would otherwise still be sitting on. Reported on every close, the "Done" and the "×" alike, since a user
  // who set up one section and dismissed the wizard has priced that section just the same.
  // A ref, not state: nothing renders from it, and the close handlers must read what the last persist wrote.
  const pricedSectionIndexRef = useRef(null);

  const stage = stages[currentStage];
  const sectionIndex = stage.sectionIndex;
  // The stages only ever host the CURRENT section's component, so the picked/selection callbacks fire for the
  // current section. A ref carries that index into the (stable) callbacks, so they never re-create -- a
  // re-created callback would re-fire ManufacturingLineLists' notify effect every render.
  const sectionIndexRef = useRef(sectionIndex);
  sectionIndexRef.current = sectionIndex;

  const handlePicked = useCallback((identifier) => {
    setPickedBySection((previous) => ({ ...previous, [sectionIndexRef.current]: identifier }));
  }, []);
  const handleSelection = useCallback((group) => {
    setSelectionBySection((previous) => ({ ...previous, [sectionIndexRef.current]: group }));
  }, []);

  // The quantity stage renders ONLY the count field, not the whole general form (which also carries windows,
  // currency and the exchange table) -- the wizard asks one thing per stage. The trimmed config keeps the
  // general section's own post-on-change wiring, so the field persists and publishes exactly as on the Manage
  // tab.
  const quantityConfig = {
    ...config.general,
    attributes: config.general.attributes.filter((attribute) => attribute.name === 'count'),
  };

  const isLastStage = currentStage === totalStages - 1;

  const canAdvance = () => {
    if (posting) { return false; }
    if (stage.type === 'quantity') { return true; }
    if (stage.type === 'line') { return Boolean(pickedBySection[sectionIndex]); }
    if (stage.type === 'solution') { return Boolean(selectionBySection[sectionIndex]); }
    return false;
  };

  // Persist + price ONE section from its marked solution -- the same select-solution POST
  // SelectedSolutionSections does on the normal page (updates window.user_quote_nav_sections and announces it),
  // so when the wizard closes the page shows the priced section.
  const persistSolution = (sectionIdx, group) => new Promise((resolve, reject) => {
    PostRequest({
      url: `/mcc/${window.user_quote_id}/select-solution/${sectionIdx}`,
      data: { lists: group },
      callback: (data) => {
        window.user_quote_nav_sections = data.window_nav_sections;
        window.dispatchEvent(new CustomEvent('navSectionsUpdated', {
          detail: { windowNavSections: data.window_nav_sections, sourceIndex: sectionIdx }
        }));
        resolve(data);
      }
    }).catch(reject);
  });

  // Closing always reports which section the wizard priced (null if it priced none), so the page can follow the
  // user to that section's Pricing tab.
  const closeWizard = () => { onClose({ pricedSectionIndex: pricedSectionIndexRef.current }); };

  const goNext = async () => {
    if (!canAdvance()) { return; }
    setPostError(false);

    // Leaving a solution stage prices that section before moving on (or closing).
    if (stage.type === 'solution') {
      setPosting(true);
      try {
        await persistSolution(sectionIndex, selectionBySection[sectionIndex]);
      } catch (error) {
        setPosting(false);
        setPostError(true);
        return; // keep the wizard open so the user can retry
      }
      pricedSectionIndexRef.current = sectionIndex;
      setPosting(false);
    }

    if (isLastStage) { closeWizard(); return; }
    setCurrentStage((previous) => previous + 1);
  };

  const goBack = () => {
    if (currentStage === 0 || posting) { return; }
    setPostError(false);
    setCurrentStage((previous) => previous - 1);
  };

  // EVERY STAGE OPENS AT ITS OWN TOP. One scroll container (the body below) is reused by every stage, so the
  // place the reader had scrolled to was still there when the next stage drew into it: leaving the
  // manufacturing-line stage 840px down landed the solution cards 840px down as well, and Back landed on a
  // listing scrolled past its filters and its first row of cards. The wizard asks one question per stage --
  // each has to start where its answer starts.
  //
  // Nothing did this by accident either way: a next stage whose first frame was SHORTER than the body (the
  // solution stage while its solve was still out) came out at the top only because the browser clamps a scroll
  // position its content can no longer reach, and one whose cards were already in hand kept the old offset.
  //
  // The stage AND NOTHING ELSE is what re-runs this: the reader scrolls within a stage while picking, and a
  // reset on any other render would pull the list out from under that. Note ManufacturingLineLists' own
  // scroll-to-the-selection deliberately stands down in the wizard (#scrollToSelectedCard), so this is the only
  // hand on the modal's scroll position.
  //
  // Before paint (useLayoutEffect), so the new stage is drawn at the top rather than drawn where the last one
  // was left and then jumped.
  const bodyRef = useRef(null);
  useLayoutEffect(() => {
    if (bodyRef.current) { bodyRef.current.scrollTop = 0; }
  }, [currentStage]);

  // THE SECTION NAME AS IT READS INSIDE THE SENTENCE. A section is labelled for a tab -- "Corrugated cardboard",
  // "Carton" -- where a capital is right; the English title then drops it MID-sentence ("Select a manufacturing
  // line for Corrugated cardboard"), where it is not.
  //
  // English only, and deliberately so: the Turkish title puts the section FIRST ("Oluklu mukavva için üretim
  // hattı seçin"), so there the capital is correct and lowering it would be the error. Restricting the change to
  // English also keeps this away from the Turkish dotted/dotless i, where a naive toLowerCase turns İ into i̇.
  //
  // Only the FIRST letter, and only when the second is already lower case: that leaves an initialism like "PVC"
  // alone rather than rendering it "pVC".
  const sectionInSentence = (label) => {
    if (window.locale !== 'en' || !label) { return label; }
    if (label.length > 1 && label[1] !== label[1].toLowerCase()) { return label; }

    return label[0].toLowerCase() + label.slice(1);
  };

  const stageTitle = () => {
    if (stage.type === 'quantity') { return t('setup_wizard.quantity_title'); }
    if (stage.type === 'line') {
      return t('setup_wizard.line_title', { section: sectionInSentence(stage.label) });
    }
    return t('setup_wizard.solution_title', { section: sectionInSentence(stage.label) });
  };

  const renderStageBody = () => {
    if (stage.type === 'quantity') {
      return (
        <div style={{ maxWidth: '360px', margin: '0 auto' }}>
          <QuoteBuilderForm config={quantityConfig} />
        </div>
      );
    }

    // The section's own flow, scoped to this stage. Keyed by section so moving BETWEEN sections remounts (each
    // section restores its own picked plan from the URL and its own selection from the server); moving between
    // this section's line and solution stages keeps the same instance, so the picked plan and its solved
    // solutions carry over.
    return (
      <LayoutNavSectionList
        key={`wizard_section_${sectionIndex}`}
        navSections={[navSections[sectionIndex]]}
        config={config}
        count={count}
        stageView={stage.type}
        inWizard
        onWizardPickedChange={handlePicked}
        onWizardSelectionChange={handleSelection}
      />
    );
  };

  // Progress fills as the user advances: empty on the first stage, full on the last. It is the modal's own top
  // edge (flush inside the card's rounded corners), so it reads as part of THIS popup rather than a second
  // detached bar.
  const progressPercent = totalStages > 1 ? (currentStage / (totalStages - 1)) * 100 : 100;

  return createPortal(
    <div style={backdropStyle}>
      <div style={modalStyle} role="dialog" aria-modal="true">
        <div style={headerStyle}>
          <span style={{ gridColumn: 1, justifySelf: 'start', fontWeight: 600, fontSize: '15px', color: 'var(--ink, #10141a)' }}>
            {t('setup_wizard.title')}
          </span>
          {/* The setup progress lives WITH the stage indicator, centred, so it is part of this one popup rather
              than a separate bar. The slim bar under the text fills as the user advances. */}
          <div style={{ gridColumn: 2, justifySelf: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <span style={{ color: 'var(--ink-faint, #8a94a3)', fontSize: '13px' }}>
              {t('setup_wizard.stage_indicator', { current: currentStage + 1, total: totalStages })}
            </span>
            <div style={{ width: '160px', height: '4px', background: 'var(--surface-muted, #f1f3f5)', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progressPercent}%`, background: 'var(--brand, #eb5424)', transition: 'width 200ms ease' }} />
            </div>
          </div>
          <button type="button" onClick={closeWizard} aria-label="Close" style={{ ...closeButtonStyle, gridColumn: 3, justifySelf: 'end' }}>×</button>
        </div>

        <div style={titleStyle}>{stageTitle()}</div>

        {/* The modal's one scrolling area -- see the stage reset above, which is what data-wizard-body names. */}
        <div ref={bodyRef} style={bodyStyle} data-wizard-body>{renderStageBody()}</div>

        {postError && (
          <div style={{ color: '#c00', fontSize: '13px', textAlign: 'center', padding: '0 20px 8px' }}>
            {t('setup_wizard.save_failed')}
          </div>
        )}

        <div style={footerStyle}>
          <button
            type="button"
            onClick={goBack}
            disabled={currentStage === 0 || posting}
            style={secondaryButtonStyle(currentStage === 0 || posting)}
          >
            {t('setup_wizard.back')}
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={!canAdvance()}
            style={primaryButtonStyle(!canAdvance())}
          >
            {posting ? '…' : (isLastStage ? t('setup_wizard.done') : t('setup_wizard.next'))}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const backdropStyle = {
  // Above the site's fixed top nav (its z-index would otherwise cover the modal header / stage indicator),
  // so the wizard is a true full-page modal. Centred both ways; the modal's maxHeight (90vh) plus the
  // backdrop's own overflow keeps the tall section stages from clipping when centred.
  position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(16, 24, 40, 0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4vh 2vw', overflow: 'auto',
};
const modalStyle = {
  position: 'relative', display: 'flex', flexDirection: 'column', width: '92vw', maxWidth: '1120px',
  maxHeight: '90vh', background: 'var(--surface, #ffffff)', borderRadius: '10px',
  boxShadow: '0 12px 48px rgba(16, 24, 40, 0.28)', overflow: 'hidden',
};
const headerStyle = {
  // A 1fr auto 1fr grid so the centre column (the stage indicator + progress) is truly centred on the modal,
  // regardless of the title / close-button widths -- space-between drifted it toward the narrower side.
  display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '16px',
  padding: '14px 20px', borderBottom: '1px solid var(--rule, #e3e7ec)',
};
const closeButtonStyle = {
  background: 'none', border: 'none', fontSize: '24px', lineHeight: 1, cursor: 'pointer',
  color: 'var(--ink-faint, #8a94a3)', padding: '0 4px',
};
const titleStyle = {
  padding: '18px 20px 6px', textAlign: 'center', fontSize: '18px', fontWeight: 600,
  color: 'var(--ink, #10141a)',
};
const bodyStyle = { flex: 1, overflow: 'auto', padding: '10px 20px 16px' };
const footerStyle = {
  display: 'flex', justifyContent: 'center', gap: '16px',
  padding: '12px 20px', borderTop: '1px solid var(--rule, #e3e7ec)',
};
const buttonBase = {
  padding: '9px 22px', fontSize: '15px', fontWeight: 600, borderRadius: '8px', cursor: 'pointer',
};
const primaryButtonStyle = (disabled) => ({
  ...buttonBase, border: 'none',
  background: disabled ? 'var(--rule, #e3e7ec)' : 'var(--brand, #eb5424)',
  color: disabled ? 'var(--ink-faint, #8a94a3)' : '#ffffff',
  cursor: disabled ? 'default' : 'pointer',
});
const secondaryButtonStyle = (disabled) => ({
  ...buttonBase, background: 'var(--surface, #ffffff)', color: 'var(--ink-soft, #4e5865)',
  border: '1px solid var(--rule, #e3e7ec)', cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.5 : 1,
});

export default FrankSetupWizard;
